"use client";

import { useDeferredValue } from "react";

import {
  ComboBox,
  ListBox,
  ListLayout,
  Popover,
  Virtualizer,
  type ComboBoxProps,
  type PopoverProps,
} from "react-aria-components";

import { InputText, type InputTextProperties } from "@jetblue/kit/input-text";
import { cn } from "../../utilities/tailwind";

export type InputComboBoxProperties = Omit<
  ComboBoxProps<object>,
  "className" | "style"
> & {
  /**
   * Additional class names to apply to the combobox container.
   */
  className?: string | undefined;

  /**
   * Properties to pass to the input element.
   */
  inputProperties?:
    | Omit<InputTextProperties, "label" | "placeholder">
    | undefined;

  /**
   * The label used for the input element.
   */
  label: string;

  /**
   * Additional class names to apply to the ListBox container.
   */
  listBoxClassName?: string | undefined;

  /**
   * Properties to pass to the popover element.
   */
  popoverProperties?: Omit<PopoverProps, "children"> | undefined;
};
export const InputComboBox = ({
  children,
  className = "",
  inputProperties,
  inputValue,
  label,
  listBoxClassName = "",
  popoverProperties = {},
  ...properties
}: InputComboBoxProperties) => {
  const deferredValue = useDeferredValue(inputValue);
  const twClassname = cn("relative flex-1", className);
  const { className: popoverClassName = "" } = popoverProperties;
  const twPopoverClassName = cn(
    "w-(--trigger-width) overflow-hidden rounded-b-md border border-medium-gray bg-white outline-none data-[placement=top]:rounded-t-md data-[placement=top]:rounded-b-none",
    popoverClassName,
  );

  const twListBoxClassName = cn(
    "max-h-40 overflow-scroll md:max-h-80",
    listBoxClassName,
  );

  return (
    <ComboBox
      className={twClassname}
      menuTrigger="focus"
      {...properties}
      inputValue={deferredValue}
    >
      <InputText label={label} selectOnFocus {...inputProperties} />

      <Popover
        offset={1}
        placement="bottom"
        {...popoverProperties}
        className={twPopoverClassName}
      >
        <Virtualizer layout={ListLayout}>
          <ListBox
            className={twListBoxClassName}
            dependencies={[deferredValue]}
          >
            {children}
          </ListBox>
        </Virtualizer>
      </Popover>
    </ComboBox>
  );
};
InputComboBox.displayName = "InputComboBox";

export { Header, type HeaderProperties } from "./header";
export { ListBoxItem, type ListBoxItemProperties } from "./listbox-item";
export {
  ListBoxSection,
  type ListBoxSectionProperties,
} from "./listbox-section";
